<script lang="ts" setup>
import './profile.scss';
import Sidebar from '@/components/sidebar/sidebar.vue';
import config from '@/config';
import { usePerformerStore } from '@/stores/performer';
import { usePeekStore } from '@/stores/peek';
import { storeToRefs } from 'pinia';
import { watch, ref, onBeforeMount, computed, watchEffect, onBeforeUnmount, nextTick, defineAsyncComponent } from 'vue';
import type { Ref } from 'vue';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/stores/user';
import { useCamStore } from '@/stores/cam';
import { useAlertsStore } from '@/stores/alerts';
import { useTeaserStore } from '@/stores/teaser';
import { usePaymentStore } from '@/stores/payment';
import { useSidebarStore } from '@/stores/sidebar';
import { useModalsStore } from '@/stores/modals';
import { useMediaStore } from '@/stores/media';
import { createPhoneReservation } from '@/api/session';
import { useLocalizationStore } from '@/stores/localization';
import { getPerformerStatus, camStatus, performerStatusColor, tabEnabled } from '@/api/performer/utils';
import type { SessionType, initiatePayload } from '@/ontology/stream';
import { format, fromUnixTime } from 'date-fns';

import favorite from '@/components/buttons/favorite/favorite.vue';
import slider from '@/components/profile/slider/slider.vue';
import agenda from '@/components/profile/agenda/agenda.vue';
import mGrid from '@/components/profile/mGrid/mGrid.vue';
import type { Performer } from '@/ontology/performer';
import i18n from './../../translations';

import { extractLocation, scrollTop, scrollBottom, scrollTabs, startFromTop, getAvatar, getSliderImages, videoUrl, isPaidMedia, performerSnapShot, transformCupSize, reArrangeMedia, numbersOnly, getStaticImages, videoPreview } from '@/utils';

import { StartupErrors } from '@/api/session/utils';
import { useSeo } from '@/composables/seo';
import { useMatomo } from '@/composables/useMatomo';

import none from './tabs/none.vue';
const cam = defineAsyncComponent(() => import('./tabs/cam.vue'));
const videocall = defineAsyncComponent(() => import('./tabs/videocall.vue'));
const voyeur = defineAsyncComponent(() => import('./tabs/voyeur.vue'));
const email = defineAsyncComponent(() => import('./tabs/email.vue'));
const phone = defineAsyncComponent(() => import('./tabs/phone.vue'));
const vipcam = defineAsyncComponent(() => import('./tabs/vipcam.vue'));

const props = defineProps<{ advert: number }>();
const performerStore = usePerformerStore();
const { performers } = storeToRefs(performerStore);
const { isFavorite, isSubscribed, getByAdvert, loadPerformer, getCurrentDesc } = performerStore;

const localizeStore = useLocalizationStore();
const { numbers, marketing, hasIVR, TabsServices } = storeToRefs(localizeStore);
const { toggleModal, closeAllModals } = useModalsStore();

const mediaStore = useMediaStore();
const { getCurrentMedia, getCurrentSliderMedia } = storeToRefs(mediaStore);
const { loadMedia } = useMediaStore();

const sessionComponent = ref(null);
const userStore = useUserStore();
const camStore = useCamStore();
const teaserStore = useTeaserStore();
const paymentStore = usePaymentStore();
const sidebarStore = useSidebarStore();

const router = useRouter();
const { setTitle, setDescription, setKeywords, setGraphData } = useSeo();
const { trackPageView, trackEvent } = useMatomo();

const { toggleSubscribe, isLoggedIn } = useUserStore();
const { account } = storeToRefs(userStore);
const { code } = storeToRefs(paymentStore);

const tabsEnabled = ref(true);
const form:Ref<{
        ivrCode:any | undefined, 
        name:string | undefined, 
        errors: {code?:boolean, name?:boolean} 
    }> = ref({
    ivrCode: paymentStore.code, 
    name: userStore.account.username,
    errors: {}
});

const activeService = ref('none');
const activePayment = ref('credits');
const displayFullDescription = ref(false);

const prevRoute = ref(null);
const performerId = ref(0);

const displaySlider = ref(false);
const displayCurrentSlide: Ref<number> = ref(0);
const isLoading = ref(false);
const hasAttemptedWithoutIvrCode = ref(false);

const theme = ref(import.meta.env.VITE_Themename);
const isDusk = computed(() => theme.value === 'dusk');

const performer = computed( () => {
    if (! performerId.value ){
        return undefined;
    }

    return performers.value[ performerId.value ]
});

const resolveFirstAvailableService = computed(() => {
    if(!performer.value || !TabsServices.value || !TabsServices.value.length) {
        return 'none';
    }
    for (const service of TabsServices.value) {
        if(tabEnabled(service, performer.value)) {
            return service;
        }
    }
    return 'none';
})

const showSlider = computed(() => {
    return userStore.mobile ? true : displaySlider.value;
});

const showCTA = computed(() => {
    let cam = (performer.value?.services['cam'].status === 'available' || performer.value?.services['vipcam'].status === 'available') && performer.value?.status != 'BUSY';
    let peek = performer.value?.services['peek'].status === 'available' && performer.value?.status === 'BUSY';
    let email = performer.value?.services['email'].status === 'available';
    return cam ? 'cam' : peek ? 'peek' : email ? 'email' : 'cam';
});

const camLabel = computed(() => {
    return performer.value?.services['peek'].status === 'available' && performer.value?.status === 'BUSY' ? 'peek' : 'cam';
});

const isCallable = computed(() => {
    return performer.value!.services['phone'].status === 'available';
});

const haveVIP = computed(() => {
    const findVIP = performer.value!.publicTags ? performer.value!.publicTags.find(obj => obj.name === 'VipActivated') : false;
    return findVIP ? true : false;
});

const allowedOrientations = [
    'heteroseksueel',
    'heteroflexibel',
    'lesbisch',
    'homoseksueel',
    'biseksueel',
    'aseksueel'
];

// Compute the orientation only if isDusk is true.
const performerOrientations = computed(() => {
    if (!isDusk.value || !performer.value?.publicTags) return [];
        return performer.value.publicTags
            .filter((tag: any) => allowedOrientations.includes(tag.name))
            .map((tag: any) => tag.name);
});

async function load() {
    startFromTop()
    
    //let's see if there's a cached version of her already..
    let p = getByAdvert( props.advert );
    if (p){
        performerId.value = p.id;
    }

    p = await loadPerformer(props.advert);
    if(p){
        performerId.value = p.id;
        setMetaData(p) ;
    }
    
    // Set media for performer
    await loadMedia(props.advert);
    activeService.value = resolveFirstAvailableService.value;
}

function selectTab(newTab: string){
    tabsEnabled.value = true
    if (tabEnabled(newTab, performer.value)){
        activeService.value = newTab;
    }
}

function sendMessage(){
    if(hasIVR.value){
        scrollTabs();
        selectTab('email');
    } else if(isLoggedIn()) {
        router.push({ name: 'inbox', params: { view: 'compose', advertNumber: performer.value?.advertNumber } });
    } else {
        toggleModal('login');
    }
}

async function initiateService(type: any) {
    if (isLoggedIn()  && userStore.isPaymentMethod() === 'credits' && type === 'videocall') {
        scrollTabs();
        selectTab('videocall');
        return;
    }
    
    if(!isLoggedIn() && userStore.isPaymentMethod() === 'credits'){
        toggleModal('login');
        trackEvent('Service', 'initiateServiceLogin', 'Login Required');
        return;
    }

    if(!isLoggedIn() && hasAttemptedWithoutIvrCode.value) {
        toggleModal('login');
        trackEvent('Service', 'initiateServiceFailedIVR', 'Login Required After Failed IVR Code');
        return;
    }

    trackEvent('Service', 'initiateStart', type);

    const payloadType = userStore.isPaymentMethod() === 'phone' ? {type, ivrCode: form.value.ivrCode, name: form.value.name} : {type: type};
    await initiate(payloadType);      
}

async function initiate(payload: initiatePayload) {
    if (!performer.value) {
        trackEvent('Service', 'initiateError', 'Performer Unknown');
        throw new Error("Performer unknown")
    }

    if (!payload.type) {
        trackEvent('Service', 'initiateError', 'Session Type Unknown');
        throw new Error("Session type unknown")
    }

    const typeSelection = payload.type != 'teaser';
    const selectedStore = typeSelection ? camStore : teaserStore; 
    form.value.errors = {};

    if ('ivrCode' in payload){
        if (!payload.name){
            useAlertsStore().errorMessage('tabs.errorWrongName');
            form.value.errors.name = true;
        }
        if (!payload.ivrCode) {
            useAlertsStore().errorMessage('tabs.errorNoIvrCode');
            form.value.errors.code = true;
            hasAttemptedWithoutIvrCode.value = true;      
        }
        if (payload.ivrCode && payload.ivrCode.length != 5 && !/\D/.test(payload.ivrCode)){ 
            useAlertsStore().errorMessage('tabs.errorWrongIvrCode');
            form.value.errors.code = true;
        }
        if (form.value.errors.name || form.value.errors.code){
            return;
        }
    }

    isLoading.value = true;
    const error = await selectedStore.initiate(performer.value.id, payload);
    isLoading.value = false;

    if (error) {
        trackEvent('Service', 'initiateFailed', error.code);
        switch( error.code ){
            case  StartupErrors.wrong_ivr_code:
                useAlertsStore().errorMessage('profile.alerts.notCorrectIvrCode', 1);
                form.value.errors.code = true;
                break;
            case  StartupErrors.broke:
                useAlertsStore().errorMessage('profile.alerts.notEnoughCredits', 1)
                // Redirect if not enough credits
                router.push({ name: 'payment' });
                break;
            case StartupErrors.double_session:
                useAlertsStore().errorMessage('profile.alerts.double', 1);
                break;
            case StartupErrors.blocked:
                useAlertsStore().errorMessage('profile.alerts.blocked', 1);
                break;
            case StartupErrors.no_code:
                useAlertsStore().errorMessage('tabs.errorNoIvrCode', 1);
                form.value.errors.code = true;
                break;
            default:
                useAlertsStore().errorMessage('profile.alerts.rest', 1);
        }

        await selectedStore.end();
    } else {
        trackEvent('Service', 'allChecksSuccessfull', payload.type);
    }
}

async function startCall(){
    if (!isCallable.value){
        return;
    }
    const { result, error } = await createPhoneReservation(performer.value!.advertNumber);

    if(error){
        useAlertsStore().errorMessage('profile.errorReservationFailed')
        return;
    }
    if (!result){
        throw new Error('impossible');
    }
    if(result.DNIS){
        window.open(`tel:${result.DNIS}`, '_self');
    }
}

watch( ()=>props.advert, async(value, oldValue)=>{
    await load();

    // Close sidebar on mobile devices when switching profile
    if((userStore.mobile && sidebarStore.collapse)){
        sidebarStore.switchState();
    }
});

watch( ()=> userStore.isLoggedIn(), async(value, oldValue)=>{
    form.value.name = userStore.account.username;
    await loadPerformer(props.advert);
});

watch(() => camStore.status, 
    (newValue, oldValue) => {
        // Toggle loader modal when connecting to performer
        toggleModal('loader', newValue === 'authorizing');
        // Redirect when camStore is initializing video or waiting on teaser to 1on1
        if(newValue === 'initializing'){
            router.push({ name: 'videochat', params: { advert: +performer.value!.advertNumber, service: camStore.type } });
        }
        if(newValue === 'awaiting-teaser-end'){
            router.push({ name: 'videochat', params: { advert: +performer.value!.advertNumber, service: 'chat' } });
        }
    }
);

watch(() => teaserStore.status,
    (newValue) => {
        if (newValue === 'active'){
            router.push( { name: 'teaser', params: { advert: +performer.value!.advertNumber } } );
        }
    }
);

watch(() => paymentStore.code,
    (newValue) => {
        form.value.ivrCode = newValue;
    }
);

// Check if active services are mutated and adjust opened tab to it
watch([() => performer.value?.services, () => TabsServices.value], () => {
    if (performer.value && TabsServices.value.length) {
        activeService.value = resolveFirstAvailableService.value;
    }
}, { immediate: true, deep: true });

onBeforeMount( async ()=>{
    await load();
    if ( !['initiating', 'initializing'].includes(camStore.status) ){
        camStore.setStatus("idle");
    }
});

watch( ()=> form.value.errors, async(value, oldValue)=>{
    setTimeout(() => {
        form.value.errors = {};
    }, 30000);
});

function toggleSlider(Id: number) {
    displayCurrentSlide.value = Id;
    displaySlider.value = true;
}

function setMetaData(performer: Performer){
    if(!performer){
        return;
    }

    setTitle(i18n.global.t('meta.title.profile', { nickname: performer.nickname }).toString());
    setDescription(i18n.global.t('meta.desc.profile', { nickname: performer.nickname }).toString());
    setKeywords(`${performer.nickname}, ${performer.eyeColor}, ${performer.cupSize}`);

    setGraphData('og:type', 'profile');
    setGraphData('og:image', getAvatar(performer, 'medium'));
    setGraphData('profile:username', performer.nickname);
    setGraphData('profile:gender', 'female');

    trackPageView();
}

async function toggleFavorites() {
    if (!performer.value) return;
    await performerStore.toggleFavorite(performer.value?.id);
}

async function toggleNotifications() {
    if (!performer.value || !account.value) return;
    await toggleSubscribe(account.value.id!, performer.value.id);
}
</script>

<template src="./profile.html"></template>